import { type ForwardRefExoticComponent, type RefAttributes, forwardRef } from 'react'

import { RecapBanner } from 'components/meetings/RecapBanner'
import { NotetakerDisplayMode } from 'features/meetings/hooks'
import type { NotetakerState } from 'features/meetings/hooks/use_notetaker_state'
import { AiRecapButton } from 'features/meetings/meeting_detail/header/ai_recap_button'
import type { Meeting } from 'features/meetings/types'

// notetaker_toggle is an untyped .js file — cast so TS sees the actual props
import { NotetakerToggle as _NotetakerToggle } from '../notetaker_toggle'

const NotetakerToggle = _NotetakerToggle as ForwardRefExoticComponent<
  {
    event: Meeting
    toggleState?: any
  } & RefAttributes<HTMLDivElement>
>

interface NotetakerControlProps {
  event: Meeting
  notetakerState: NotetakerState
}

/**
 * Unified router component for notetaker toggle and recap UI states.
 * Routes to appropriate child component based on displayMode.
 * Receives pre-computed notetakerState from parent (MeetingCardContent).
 */
export const NotetakerControl = forwardRef<HTMLDivElement | null, NotetakerControlProps>(({
  event,
  notetakerState,
}, ref) => {
  const { displayMode, noRecapTooltipKey, toggleState } = notetakerState

  switch (displayMode) {
    case NotetakerDisplayMode.TOGGLE_ON:
    case NotetakerDisplayMode.TOGGLE_OFF:
      return (
        <NotetakerToggle
          ref={ref}
          event={event}
          toggleState={toggleState}
        />
      )

    case NotetakerDisplayMode.RECAP_PROCESSING:
      return <RecapBanner variant="processing" event={event} />

    case NotetakerDisplayMode.NO_RECAP_AVAILABLE:
      return <RecapBanner variant="no-recap" tooltipKey={noRecapTooltipKey!} event={event} />

    case NotetakerDisplayMode.RECAP_BUTTON:
      return <AiRecapButton event={event} location="meetings_page" />

    case NotetakerDisplayMode.RECAP_ERROR:
      return <RecapBanner variant="error" event={event} />

    default:
      return null
  }
})
